import React, { useEffect, useState } from 'react';
import { ArticleTocItem } from '../../types';
import { formatReadingTime, toFaDigits } from '../../utils/blogFormat';
import BlogIcon from './BlogIcon';
import { useBlogSettings } from './blogSettings';

interface TableOfContentsProps {
  items: ArticleTocItem[];
  readingTime?: number | null;
  quoteText?: string | null;
  quoteAuthor?: string | null;
}

/** با IntersectionObserver سرفصل فعال را دنبال می‌کند؛ تا رسیدن به اولین سرفصل هیچ‌کدام فعال نیست (مثل طرح موبایل) */
const useActiveHeading = (ids: string[]) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  useEffect(() => {
    if (!ids.length || typeof IntersectionObserver === 'undefined') return undefined;
    const visible = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.set(entry.target.id, entry.boundingClientRect.top);
          else visible.delete(entry.target.id);
        });
        if (visible.size) {
          const top = Array.from(visible.entries()).sort((a, b) => a[1] - b[1])[0][0];
          setActiveId(top);
        }
      },
      { rootMargin: '-96px 0px -60% 0px', threshold: 0 },
    );
    const nodes = ids.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join('|')]);
  return activeId;
};

const scrollTo = (id: string) => (e: React.MouseEvent) => {
  const el = document.getElementById(id);
  if (!el) return;
  e.preventDefault();
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.history.replaceState(null, '', `#${id}`);
};

/** فهرست مطالب دسکتاپ (ستون چسبان راست) + کارت نقل‌قول */
export const TableOfContentsSidebar: React.FC<TableOfContentsProps> = ({ items, readingTime, quoteText, quoteAuthor }) => {
  const { tocTitle } = useBlogSettings();
  const headings = items.filter((i) => i.level === 2);
  const activeId = useActiveHeading(headings.map((h) => h.id));

  return (
    <div className="flex flex-col gap-4">
      {headings.length > 0 && (
        <nav aria-label="فهرست مطالب" className="flex flex-col gap-4 rounded-[12px] border border-[#e9e8e5] bg-white p-4 drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-2 border-b border-[#e9e8e5] pb-2">
            <span className="h-2 w-2 rounded-full bg-[#331751]" aria-hidden />
            <h2 className="text-[18px] font-semibold leading-[30px] text-[#1a1c1a]">{tocTitle}</h2>
          </div>
          <ol className="flex flex-col gap-1 pb-2">
            {headings.map((h) => {
              const active = h.id === activeId;
              return (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    onClick={scrollTo(h.id)}
                    aria-current={active ? 'true' : undefined}
                    className={`block rounded-[4px] text-[14px] leading-[26px] transition-colors ${
                      active ? 'border-r-2 border-[#4a2e68] bg-[#f4f3f1] py-2 pl-2 pr-3 font-semibold text-[#331751]' : 'p-2 text-[#5b5f66] hover:bg-[#f4f3f1] hover:text-[#331751]'
                    }`}
                  >
                    {h.text}
                  </a>
                </li>
              );
            })}
          </ol>
          <div className="flex items-center justify-between border-t border-[#e9e8e5] pt-4 text-[12px] font-medium leading-5">
            <span className="text-[#5b5f66]">مدت زمان تخمینی</span>
            <span className="text-[#1a1c1a]">{formatReadingTime(readingTime, '')}</span>
          </div>
        </nav>
      )}

      {quoteText && (
        <blockquote className="flex flex-col gap-2 rounded-[12px] border border-[#e9e8e5] bg-[#f4f3f1] px-4 pb-4 pt-[15px]">
          <p className="text-[14px] leading-[22.75px] text-[#4a454e]">{quoteText}</p>
          {quoteAuthor && <footer className="text-[12px] font-medium leading-5 text-[#5b5f66]">— {quoteAuthor}</footer>}
        </blockquote>
      )}
    </div>
  );
};

/** فهرست مطالب جمع‌شوندهٔ موبایل (بالای متن) */
export const TableOfContentsCollapsible: React.FC<{ items: ArticleTocItem[] }> = ({ items }) => {
  const { tocTitle } = useBlogSettings();
  const headings = items.filter((i) => i.level === 2);
  // طرح موبایل فهرست را باز نشان می‌دهد
  const [open, setOpen] = useState(true);
  const activeId = useActiveHeading(headings.map((h) => h.id));
  if (!headings.length) return null;

  return (
    <section
      aria-label="فهرست مطالب"
      className="overflow-hidden rounded-[12px] border border-[#e9e8e5] bg-[#f4f3f1]"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between p-4 text-right"
      >
        <span className="inline-flex items-center gap-2 text-[18px] font-semibold leading-[30px] text-[#331751]">
          <BlogIcon name="toc-list" width={15} height={13.333} />
          {tocTitle}
        </span>
        <BlogIcon name="toc-chevron" width={10} height={6.167} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ol className="flex flex-col gap-[10px] border-t border-[#e9e8e5] px-4 pb-4 pt-1 pr-6">
          {headings.map((h, index) => {
            const active = h.id === activeId;
            return (
              <li key={h.id} className="flex items-start gap-2">
                <span className="text-[14px] font-semibold leading-[26px] text-[#4a2e68]">{toFaDigits(index + 1)}.</span>
                <a
                  href={`#${h.id}`}
                  onClick={(e) => {
                    scrollTo(h.id)(e);
                    setOpen(false);
                  }}
                  className={`text-[14px] leading-[26px] ${active ? 'font-semibold text-[#4a2e68]' : 'text-[#5b5f66]'}`}
                >
                  {h.text.replace(/^[\d۰-۹]+[.．،-]\s*/, '')}
                </a>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};
